import styled, { keyframes } from "styled-components";
import { COLORS, THEME_FALLBACKS, INDIGO_CSS_VARS } from "../../common/colors";

// ─── Keyframes ─────────────────────────────────────────────────────────────────

export const kFadeUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0);    }
`;

export const kSlideIn = keyframes`
  from { opacity: 0; transform: translateX(-20px); }
  to   { opacity: 1; transform: translateX(0);     }
`;

export const kSlideInRight = keyframes`
  from { opacity: 0; transform: translateX(20px); }
  to   { opacity: 1; transform: translateX(0);    }
`;

export const kScaleIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to   { opacity: 1; transform: scale(1);    }
`;

export const kFloat = keyframes`
  0%, 100% { transform: translateY(0px);  }
  50%       { transform: translateY(-10px); }
`;

export const kPulseRing = keyframes`
  0%   { transform: scale(0.95); box-shadow: 0 0 0 0   ${COLORS.primaryGlow}; }
  70%  { transform: scale(1);    box-shadow: 0 0 0 14px ${COLORS.primaryGlow}; }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0   ${COLORS.primaryGlow}; }
`;

export const kRotate = keyframes`
  from { transform: rotate(0deg);   }
  to   { transform: rotate(360deg); }
`;

export const kShimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
`;

// ─── Section ───────────────────────────────────────────────────────────────────

export const Section = styled.section`
  /* All design tokens come from the centralised color palette */
  ${INDIGO_CSS_VARS}

  /* Runtime theme overrides (kept for ThemeProvider compatibility) */
  --c-accent:  ${({ theme }) => theme.buttonColor      ?? THEME_FALLBACKS.buttonColor};
  --c-text:    ${({ theme }) => theme.TextColorBlack   ?? THEME_FALLBACKS.TextColorBlack};
  --c-bg:      ${({ theme }) => `rgba(${theme.body},1)` ?? COLORS.pageBg};

  --r:    1rem;
  --r-lg: 1.6rem;
  --r-xl: 2.4rem;

  --s-sm:  0 2px 14px rgba(0,0,0,0.07);
  --s:     0 4px 28px rgba(0,0,0,0.10);
  --s-lg:  0 8px 48px rgba(0,0,0,0.14);
  --s-xl:  0 16px 64px rgba(0,0,0,0.18);

  --t:      0.22s ease;
  --t-slow: 0.40s ease;

  position: relative;
  width: 100%;
  background-color: var(--c-bg);
  overflow: hidden;
  padding-top: 0;
  padding-bottom: 5rem;
  margin-top: 0;
`;
Section.displayName = "Section";

/* Decorative background blobs */
export const BgBlob = styled.span`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;

  &.b1 {
    width: 560px; height: 560px;
    top: -180px; right: -160px;
    background: radial-gradient(circle, var(--c-accent) 0%, transparent 68%);
    opacity: 0.06; filter: blur(70px);
  }
  &.b2 {
    width: 380px; height: 380px;
    bottom: -100px; left: -110px;
    background: radial-gradient(circle, ${COLORS.accentDark} 0%, transparent 68%);
    opacity: 0.07; filter: blur(60px);
  }
  &.b3 {
    width: 260px; height: 260px;
    top: 50%; left: 48%;
    background: radial-gradient(circle, ${COLORS.primaryLight} 0%, transparent 68%);
    opacity: 0.05; filter: blur(50px);
  }

  @media (max-width: 48em) {
    &.b1 { width: 300px; height: 300px; }
    &.b2 { width: 220px; height: 220px; }
    &.b3 { display: none; }
  }
`;
BgBlob.displayName = "BgBlob";

/* Decorative dotted grid overlay */
export const DotGrid = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-image: radial-gradient(circle, ${COLORS.primaryTint} 1px, transparent 1px);
  background-size: 32px 32px;
  opacity: 0.5;
`;
DotGrid.displayName = "DotGrid";

// ─── Inner wrapper ────────────────────────────────────────────────────────────

export const Inner = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem 0;

  @media (min-width: 48em) { padding: 3rem 2rem 0; }
  @media (min-width: 64em) { padding: 4rem 3rem 0; }
`;
Inner.displayName = "Inner";

// ─── Two-column layout ────────────────────────────────────────────────────────

export const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem 5rem;
  align-items: center;

  @media (max-width: 64em) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;
TwoCol.displayName = "TwoCol";

// ─── Carousel / media column ──────────────────────────────────────────────────

export const MediaCol = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${kSlideIn} 0.7s ease both 0.1s,
               ${kFloat} 6s ease-in-out 0.8s infinite;
  }
`;
MediaCol.displayName = "MediaCol";

/* Glowing ring that sits behind the carousel */
export const MediaRing = styled.div`
  position: absolute;
  inset: -24px;
  border-radius: var(--r-xl);
  background: linear-gradient(135deg, var(--c-accent), ${COLORS.primaryDark});
  opacity: 0.08;
  filter: blur(28px);
  pointer-events: none;
  z-index: 0;
`;
MediaRing.displayName = "MediaRing";

export const MediaWrap = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  border-radius: var(--r-xl);
  overflow: visible;
  box-shadow: var(--s-lg);

  @media (prefers-reduced-motion: no-preference) {
    animation: ${kFloat} 6s ease-in-out infinite;
  }
`;
MediaWrap.displayName = "MediaWrap";

/* Floating accent badge on the media card */
export const MediaBadge = styled.div`
  position: absolute;
  bottom: 1.4rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--c-accent);
  background: var(--c-surface-hi);
  border: 1px solid var(--c-glow);
  border-radius: 999px;
  padding: 0.4rem 1.1rem;
  box-shadow: var(--s);
  backdrop-filter: blur(12px);
`;
MediaBadge.displayName = "MediaBadge";

// ─── Content column ───────────────────────────────────────────────────────────

export const ContentCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;

  @media (max-width: 64em) { align-items: center; text-align: center; }

  @media (prefers-reduced-motion: no-preference) {
    animation: ${kSlideInRight} 0.7s ease both 0.2s;
  }
`;
ContentCol.displayName = "ContentCol";

export const Eyebrow = styled.span`
  display: block;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #3d6b4f;
  margin-bottom: 0.75rem;
`;
Eyebrow.displayName = "Eyebrow";

export const Title = styled.h2`
  font-size: clamp(1.8rem, 5vw, 3rem);
  font-weight: 800;
  color: #2e1f0f;
  letter-spacing: -0.03em;
  line-height: 1.1;
  margin: 0 0 0.3rem;
`;
Title.displayName = "Title";

export const SubTitle = styled.h3`
  font-size: clamp(1.8rem, 5vw, 2.9rem);
  font-weight: 900;
  color: var(--c-accent);
  letter-spacing: -0.03em;
  line-height: 1.1;
  margin: 0 0 1.4rem;
`;
SubTitle.displayName = "SubTitle";

/* Gradient accent bar matching site-wide header style */
export const Divider = styled.div`
  width: 56px;
  height: 4px;
  background: linear-gradient(90deg, #3d6b4f 0%, #7a9e4e 50%, #c9a84c 100%);
  border-radius: 999px;
  margin-bottom: 1.8rem;

  @media (max-width: 64em) { margin-left: auto; margin-right: auto; }
`;
Divider.displayName = "Divider";

export const SubText = styled.p`
  font-size: clamp(0.9rem, 2.2vw, 1.05rem);
  color: var(--c-text);
  line-height: 1.88;
  opacity: 0.8;
  margin: 0 0 1rem;
  max-width: 520px;

  @media (max-width: 64em) { max-width: 100%; }
`;
SubText.displayName = "SubText";

export const SubTextLight = styled.p`
  font-size: clamp(0.82rem, 2vw, 0.95rem);
  color: var(--c-text);
  line-height: 1.85;
  opacity: 0.6;
  margin: 0 0 2rem;
  max-width: 520px;

  @media (max-width: 64em) { max-width: 100%; }
`;
SubTextLight.displayName = "SubTextLight";

// ─── Pill stats row ───────────────────────────────────────────────────────────

export const StatRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 2rem;

  @media (max-width: 64em) { justify-content: center; }
`;
StatRow.displayName = "StatRow";

export const StatPill = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--c-surface);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid var(--c-border);
  border-radius: var(--r-lg);
  padding: 0.75rem 1.25rem;
  box-shadow: var(--s-sm);
  min-width: 90px;
  transition: transform var(--t), box-shadow var(--t);

  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--s);
  }
`;
StatPill.displayName = "StatPill";

export const StatNumber = styled.span`
  font-size: 1.3rem;
  font-weight: 900;
  color: var(--c-accent);
  letter-spacing: -0.03em;
  line-height: 1;
`;
StatNumber.displayName = "StatNumber";

export const StatLabel = styled.span`
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--c-text);
  opacity: 0.45;
  margin-top: 0.25rem;
`;
StatLabel.displayName = "StatLabel";

// ─── CTA button ───────────────────────────────────────────────────────────────

export const CtaBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.85rem 2.2rem;
  border-radius: 999px;
  border: none;
  background: linear-gradient(135deg, var(--c-accent), ${COLORS.primaryDark});
  color: ${COLORS.white};
  font-size: 0.95rem;
  font-weight: 800;
  cursor: pointer;
  min-height: 50px;
  white-space: nowrap;
  box-shadow: 0 6px 22px ${COLORS.primaryShadow};
  transition: transform var(--t), box-shadow var(--t), filter var(--t);

  svg { width: 16px; height: 16px; flex-shrink: 0; transition: transform var(--t); }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 32px ${COLORS.primaryGlow};
    filter: brightness(1.08);
  }
  &:hover svg { transform: translateX(4px); }
  &:active  { transform: translateY(0) scale(0.97); }
  &:focus-visible { outline: 2px solid var(--c-accent); outline-offset: 3px; }
`;
CtaBtn.displayName = "CtaBtn";

export const BtnRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 64em) { justify-content: center; }
`;
BtnRow.displayName = "BtnRow";